/**
 * The one rule for following a redirectUrl taken from the query string.
 *
 * A same-site path only. A "starts with / and not //" check is not enough:
 * browsers read a backslash as a slash, so "/\evil.com" leaves the site, and an
 * encoded one ("/%5Cevil.com") reaches Nuxt's navigateTo, which throws a 500
 * ("external URL is not allowed"). So the value is refused, and the fallback
 * used, when ANY decoding of it (percent-decoded until it stops changing):
 *   - does not start with "/", or starts with "//";
 *   - holds a backslash;
 *   - holds a control character anywhere (a tab or newline is dropped by URL
 *     parsers, turning "/\t/evil.com" into "//evil.com"), or whitespace in the
 *     PATH part (spaces are fine in the query and fragment);
 *   - cannot be decoded at all.
 * Pure, no Nuxt imports, so it is testable and shared by every reader:
 * useAccountSetup, pages/[authType].vue, pages/prompt.vue,
 * pages/details-required.vue and RegisterForm.
 */
const CONTROL_OR_BACKSLASH = /[\\\u0000-\u001f\u007f]/;
const PATH_UNSAFE = /[\\\s\u0000-\u001f\u007f]/;

// Every decoding of a string, the raw one first, or null when one fails.
function decodings(value) {
  const out = [value];
  let current = value;
  for (let i = 0; i < 5; i++) {
    let decoded;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      return null;
    }
    if (decoded === current) return out;
    out.push(decoded);
    current = decoded;
  }
  return null; // still changing after five decodings: nobody means that
}

export function safeRedirectPath(value, fallback = '/') {
  if (typeof value !== 'string' || !value) return fallback;
  // The whole value: no backslash or control character in any decoding.
  const whole = decodings(value);
  if (!whole || whole.some(v => CONTROL_OR_BACKSLASH.test(v))) return fallback;
  // The PATH (before the first ? or #): a same-site path in every decoding, no
  // whitespace. The query and fragment may carry encoded spaces
  // ("/clubs?q=New%20Parks" is an ordinary link).
  const path = value.split(/[?#]/, 1)[0];
  const paths = decodings(path);
  if (!paths || paths.some(p => !p.startsWith('/') || p.startsWith('//') || PATH_UNSAFE.test(p))) {
    return fallback;
  }
  return value;
}
